import { useEffect, useRef, useState } from "react";
import "./ScrollToTopButton.css";

function ScrollToTopButton() {
  const [isTopVisible, setIsTopVisible] = useState(false);
  const [isBottomVisible, setIsBottomVisible] = useState(false);
  const scrollFrameRef = useRef(null);
  const holdTimeoutRef = useRef(null);
  const pressStartRef = useRef(0);
  const suppressClickRef = useRef(false);

  useEffect(() => {
    const handleScroll = () => {
      const scrollTop = window.scrollY;
      const viewportHeight = window.innerHeight;
      const documentHeight = document.documentElement.scrollHeight;

      setIsTopVisible(scrollTop > 240);
      setIsBottomVisible(scrollTop + viewportHeight < documentHeight - 240);
    };

    handleScroll();
    window.addEventListener("scroll", handleScroll, { passive: true });

    return () => {
      window.removeEventListener("scroll", handleScroll);
      if (holdTimeoutRef.current) {
        window.clearTimeout(holdTimeoutRef.current);
      }
      if (scrollFrameRef.current) {
        window.cancelAnimationFrame(scrollFrameRef.current);
      }
    };
  }, []);

  const stopContinuousScroll = () => {
    if (holdTimeoutRef.current) {
      window.clearTimeout(holdTimeoutRef.current);
      holdTimeoutRef.current = null;
    }

    if (scrollFrameRef.current) {
      window.cancelAnimationFrame(scrollFrameRef.current);
      scrollFrameRef.current = null;
    }

    if (pressStartRef.current && Date.now() - pressStartRef.current > 220) {
      suppressClickRef.current = true;
    }

    pressStartRef.current = 0;
  };

  const startContinuousScroll = (direction, speed) => {
    stopContinuousScroll();
    pressStartRef.current = Date.now();
    suppressClickRef.current = false;

    const step = () => {
      window.scrollBy({ top: direction * speed, behavior: "auto" });
      scrollFrameRef.current = window.requestAnimationFrame(step);
    };

    holdTimeoutRef.current = window.setTimeout(() => {
      scrollFrameRef.current = window.requestAnimationFrame(step);
    }, 220);
  };

  const handleButtonClick = (action) => {
    if (suppressClickRef.current) {
      suppressClickRef.current = false;
      return;
    }

    action();
  };

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const scrollToBottom = () => {
    window.scrollTo({ top: document.documentElement.scrollHeight, behavior: "smooth" });
  };

  return (
    <div className="scroll-button-stack">
      <button
        type="button"
        className={`scroll-top-button scroll-bottom-button ${isBottomVisible ? "is-visible" : ""}`}
        onClick={() => handleButtonClick(scrollToBottom)}
        onPointerDown={() => startContinuousScroll(1, 13)}
        onPointerUp={stopContinuousScroll}
        onPointerLeave={stopContinuousScroll}
        onPointerCancel={stopContinuousScroll}
        aria-label="Scroll to bottom"
        title="Scroll to bottom"
      >
        <span className="scroll-button-icon scroll-button-icon-flow">↓</span>
      </button>

      <button
        type="button"
        className={`scroll-top-button ${isTopVisible ? "is-visible" : ""}`}
        onClick={() => handleButtonClick(scrollToTop)}
        onPointerDown={() => startContinuousScroll(-1, 9)}
        onPointerUp={stopContinuousScroll}
        onPointerLeave={stopContinuousScroll}
        onPointerCancel={stopContinuousScroll}
        aria-label="Scroll to top"
        title="Back to top"
      >
        <span className="scroll-button-icon">↑</span>
      </button>
    </div>
  );
}

export default ScrollToTopButton;